import { Component, lazy, Suspense, type ReactNode } from 'react'
import type { LetterBox } from '../Ballpit/letterPegs'
import type { ScoreMark } from '../Ballpit'
import { hasWebGL } from '../../lib/webgl'

// Three.js only loads after the title and icons are on screen.
const Ballpit = lazy(() => import('../Ballpit').then((m) => ({ default: m.Ballpit })))

/** If Three.js throws (no GPU, blocked WebGL), the rest of the page must survive, and the DOM title takes over. */
class BallpitBoundary extends Component<{ children: ReactNode; onFail: () => void }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error: unknown) {
    console.warn('Ballpit disabled:', error)
    this.props.onFail()
  }
  render() {
    return this.state.failed ? null : this.props.children
  }
}

interface SafeBallpitProps {
  count: number
  minSize: number
  maxSize: number
  size0: number
  getLetters: () => LetterBox[]
  calm?: boolean
  onToss: () => void
  onScore: (hooked: number, gains: ScoreMark[], losses: ScoreMark[]) => void
  onReady: () => void
  /** The scene threw and will not run. */
  onFail: () => void
  onControls: (controls: { setPaused: (paused: boolean) => void }) => void
  onStar: (at: ScoreMark) => void
}

/** Renders the ball pit only where it can run; otherwise renders nothing, quietly. */
export const SafeBallpit: React.FC<SafeBallpitProps> = ({ onFail, ...props }) => {
  if (!hasWebGL()) return null
  return (
    <BallpitBoundary onFail={onFail}>
      <Suspense fallback={null}>
        <Ballpit {...props} onFail={onFail} />
      </Suspense>
    </BallpitBoundary>
  )
}
